/**
 * Pure helpers for the App versions page + dialog. Everything here mirrors a rule the identity
 * backend enforces (`AppVersionPolicyValidator` / `AppVersionPolicyAuthorization`); the console
 * only gates UX with them, the backend is the authority.
 */
import type { AppPlatform, AppVersionPolicyDoc } from "@/firestore/appVersionPolicies";
import { formatTimestamp } from "@/lib/datetime";

export const PLATFORMS: readonly AppPlatform[] = ["ios", "android"];
export const PLATFORM_LABELS: Record<AppPlatform, string> = { ios: "iOS", android: "Android" };
/** The build identifier compared on each platform. */
export const BUILD_FIELD_LABELS: Record<AppPlatform, string> = {
  ios: "CFBundleVersion",
  android: "versionCode",
};
/** Where "100 % of the platform" downloads a build from — used in the availability attestation. */
export const STORE_AVAILABILITY_LABELS: Record<AppPlatform, string> = {
  ios: "fully available on the App Store",
  android: "at 100 % rollout on Google Play",
};

/** Integers `0 ≤ requiredBuild ≤ recommendedBuild ≤ 10^9` (identity validator). */
export const MAX_BUILD = 1_000_000_000;
/** A raise above `max(previous required, previous recommended) + 1000` must be retyped. */
export const MAX_UNCONFIRMED_RAISE = 1_000;
export const MAX_GRACE_DAYS = 90;
export const MAX_MESSAGE_LENGTH = 280;
export const MAX_STORE_URL_LENGTH = 512;
export const MAX_REASON_LENGTH = 500;
export const STORE_URL_HOSTS: readonly string[] = [
  "apps.apple.com",
  "testflight.apple.com",
  "play.google.com",
];
const DAY_MS = 24 * 60 * 60 * 1000;
/** A required floor that bites within 24 h is "short grace" (extra attestation in production). */
export const SHORT_GRACE_MS = DAY_MS;

/** The server's 403 wording, mirrored on the disabled submit button. */
export const SUPER_ADMIN_HINT =
  "Raising the required build or shortening the grace period needs the superAdmin role.";

export function validBuild(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= MAX_BUILD
  );
}

/** Mantine `NumberInput` yields `number | ""`; normalise to a valid build or null. */
export function parseBuild(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return validBuild(n) ? n : null;
}

/** Blank is fine (compiled store link); otherwise https on an allow-listed host, ≤ 512 chars. */
export function validStoreUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.length > MAX_STORE_URL_LENGTH) return false;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }
  return url.protocol === "https:" && STORE_URL_HOSTS.includes(url.hostname);
}

export function validMessage(value: string): boolean {
  return value.trim().length <= MAX_MESSAGE_LENGTH;
}

export function validReason(value: string): boolean {
  const length = value.trim().length;
  return length >= 1 && length <= MAX_REASON_LENGTH;
}

/** The instant a required floor becomes active. Null — or a date already passed — means now. */
export function effectiveInstant(
  requiredEffectiveAt: string | null | undefined,
  now: number,
): number {
  if (!requiredEffectiveAt) return now;
  const parsed = Date.parse(requiredEffectiveAt);
  return Number.isFinite(parsed) ? Math.max(parsed, now) : now;
}

/** Raising the hard floor (or creating one above 0) needs `superAdmin`. */
export function isRaise(
  current: Pick<AppVersionPolicyDoc, "requiredBuild"> | null,
  requiredBuild: number,
): boolean {
  return requiredBuild > (current?.requiredBuild ?? 0);
}

/** Moving the activation instant earlier than it is today needs `superAdmin`. */
export function shortensGrace(
  current: Pick<AppVersionPolicyDoc, "requiredEffectiveAt"> | null,
  requiredEffectiveAt: string | null,
  now: number,
): boolean {
  if (!current) return false;
  return (
    effectiveInstant(requiredEffectiveAt, now) < effectiveInstant(current.requiredEffectiveAt, now)
  );
}

/** Typo guard: a raise of more than 1 000 over the previous floors — or any create above 0. */
export function isLargeJump(
  current: Pick<AppVersionPolicyDoc, "requiredBuild" | "recommendedBuild"> | null,
  requiredBuild: number,
): boolean {
  if (!current) return requiredBuild > 0;
  return (
    requiredBuild >
    Math.max(current.requiredBuild, current.recommendedBuild) + MAX_UNCONFIRMED_RAISE
  );
}

export function isShortGrace(requiredEffectiveAt: string | null, now: number): boolean {
  return effectiveInstant(requiredEffectiveAt, now) < now + SHORT_GRACE_MS;
}

export type GracePreset = "keep" | "now" | "24h" | "48h" | "7d" | "14d" | "custom";

const PRESET_OFFSETS_MS: Partial<Record<GracePreset, number>> = {
  "24h": DAY_MS,
  "48h": 2 * DAY_MS,
  "7d": 7 * DAY_MS,
  "14d": 14 * DAY_MS,
};

/** `NativeSelect` options; "keep" only exists when editing an existing policy. */
export function gracePresets(hasCurrent: boolean): { value: GracePreset; label: string }[] {
  return [
    ...(hasCurrent ? [{ value: "keep" as const, label: "Keep the current date" }] : []),
    { value: "now", label: "Now (no grace)" },
    { value: "24h", label: "In 24 hours" },
    { value: "48h", label: "In 48 hours" },
    { value: "7d", label: "In 7 days (default when raising)" },
    { value: "14d", label: "In 14 days" },
    { value: "custom", label: "Custom date and time" },
  ];
}

/** `+7d` whenever the change raises the floor; otherwise keep an existing date (or "now" on create). */
export function defaultGracePreset(raising: boolean, hasCurrent: boolean): GracePreset {
  if (raising) return "7d";
  return hasCurrent ? "keep" : "now";
}

export type ResolvedGrace =
  | { kind: "keep" }
  | { kind: "value"; value: string | null }
  | { kind: "invalid"; reason: string };

/** Turns a preset (+ the custom `datetime-local` value) into the `requiredEffectiveAt` to send. */
export function resolveGrace(preset: GracePreset, custom: string, now: number): ResolvedGrace {
  if (preset === "keep") return { kind: "keep" };
  if (preset === "now") return { kind: "value", value: null };
  const offset = PRESET_OFFSETS_MS[preset];
  if (offset !== undefined) return { kind: "value", value: new Date(now + offset).toISOString() };
  const parsed = Date.parse(custom);
  if (!custom.trim() || !Number.isFinite(parsed)) {
    return { kind: "invalid", reason: "Enter a valid date and time." };
  }
  if (parsed > now + MAX_GRACE_DAYS * DAY_MS) {
    return { kind: "invalid", reason: `The grace period cannot exceed ${MAX_GRACE_DAYS} days.` };
  }
  return { kind: "value", value: new Date(parsed).toISOString() };
}

export function formatEffectiveAt(value: string | null, now: number): string {
  return effectiveInstant(value, now) <= now ? "now" : formatTimestamp(value);
}

export interface PolicyValues {
  requiredBuild: number;
  recommendedBuild: number;
  requiredEffectiveAt: string | null;
}

/** One-line delta the operator confirms, e.g. "iOS: required 500 → 540 (raise), effective now → …". */
export function describeChange(
  platform: AppPlatform,
  current: PolicyValues | null,
  next: PolicyValues,
  now: number,
): string {
  const label = PLATFORM_LABELS[platform];
  if (!current) {
    return next.requiredBuild > 0
      ? `Create the ${label} policy: builds below ${next.requiredBuild} are blocked from ${formatEffectiveAt(next.requiredEffectiveAt, now)}; builds below ${next.recommendedBuild} are nudged.`
      : `Create the ${label} policy with no hard gate (required 0, recommended ${next.recommendedBuild}).`;
  }
  const parts: string[] = [];
  if (next.requiredBuild !== current.requiredBuild) {
    parts.push(
      `required ${current.requiredBuild} → ${next.requiredBuild} (${next.requiredBuild > current.requiredBuild ? "raise" : "lower"})`,
    );
  }
  if (next.recommendedBuild !== current.recommendedBuild) {
    parts.push(`recommended ${current.recommendedBuild} → ${next.recommendedBuild}`);
  }
  if ((next.requiredEffectiveAt ?? null) !== (current.requiredEffectiveAt ?? null)) {
    parts.push(
      `effective ${formatEffectiveAt(current.requiredEffectiveAt, now)} → ${formatEffectiveAt(next.requiredEffectiveAt, now)}`,
    );
  }
  if (parts.length === 0) return `No change to the ${label} policy.`;
  return `${label}: ${parts.join(", ")}.`;
}

export interface GateStatus {
  state: "off" | "pending" | "active";
  label: string;
  color: string;
}

/** Card badge: no gate / grace still running / blocking now. */
export function gateStatus(
  doc: Pick<AppVersionPolicyDoc, "requiredBuild" | "requiredEffectiveAt"> | null,
  now: number,
): GateStatus {
  if (!doc || doc.requiredBuild === 0)
    return { state: "off", label: "No hard gate", color: "gray" };
  if (effectiveInstant(doc.requiredEffectiveAt, now) > now) {
    return {
      state: "pending",
      label: `Grace until ${formatTimestamp(doc.requiredEffectiveAt)}`,
      color: "yellow",
    };
  }
  return { state: "active", label: `Blocking builds below ${doc.requiredBuild}`, color: "red" };
}

/** The live document for a platform (newest `updatedAt` wins if the collection ever holds two). */
export function livePolicyFor<T extends Pick<AppVersionPolicyDoc, "platform" | "updatedAt">>(
  docs: readonly T[],
  platform: AppPlatform,
): T | null {
  let best: T | null = null;
  for (const doc of docs) {
    if (doc.platform !== platform) continue;
    if (!best || Date.parse(doc.updatedAt) > Date.parse(best.updatedAt)) best = doc;
  }
  return best;
}

/** Cache key for the audit `GET`: refetch whenever any policy changes. */
export function latestUpdatedAt(docs: readonly Pick<AppVersionPolicyDoc, "updatedAt">[]): string {
  let latest = "";
  for (const doc of docs) if (doc.updatedAt > latest) latest = doc.updatedAt;
  return latest || "none";
}

export function policyActionError(error: unknown): string {
  return error instanceof Error ? error.message : "The policy could not be saved.";
}
